// src/components/FilingPage.js
import React, { useState } from 'react';
import { Send, FileText, Download, Briefcase, Lock, Unlock, CheckCircle } from 'react-feather';
import { generate1040 } from '../utils/pdfGenerator';
import { verifyGumroadLicense } from '../utils/licenseManager'; 
import PersonalInfo from './PersonalInfo';

function FilingPage({ totals, formData, scheduleCData, investmentsData, scheduleEData, onDataChange }) {
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  
  // --- LICENSE STATE ---
  const [licenseKey, setLicenseKey] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isPro, setIsPro] = useState(() => {
    return !!localStorage.getItem('taxflow_license_key');
  });

  // Safety Checks
  if (!formData || !totals) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading filing data...</div>;
  }

  const preparer = formData.paidPreparer || {}; 
  const isRefund = (totals.finalTaxOwed || 0) < 0; 

  const handleRadioChange = (e, section) => {
    const booleanValue = e.target.value === 'true';
    onDataChange({
      target: {
        name: `${section}`,
        value: booleanValue,
        type: 'radio'
      }
    });
  };

  const formatCurrency = (value) => `$${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // --- UNLOCK HANDLER ---
  const handleUnlock = async () => {
    if (!licenseKey.trim()) return;
    
    setIsValidating(true);
    const isValid = await verifyGumroadLicense(licenseKey);
    setIsValidating(false);

    if (isValid) {
      setIsPro(true);
      alert("Success! Pro features unlocked.");
    } else {
      alert("Invalid license key. Please check your email.");
    }
  };

  const handleDownloadPdf = async (forceDraft) => {
    setIsPdfLoading(true);
    
    try {
      let isVerifiedPaid = false;

      
        const storedKey = localStorage.getItem('taxflow_license_key');
        
        if (storedKey) {
          // CHANGE THIS: Access the .valid property from the object
          const verification = await verifyGumroadLicense(storedKey);
          isVerifiedPaid = verification.valid; 
        }

        if (!isVerifiedPaid) {
          alert("License verification failed or key is invalid. Downloading watermarked draft instead.");
        }
      

      const fullData = {
        ...formData,
        scheduleCData: scheduleCData || [],
        scheduleEData: scheduleEData || {}
      };
      
      // Pass the boolean to your generator
      // CORRECT
      await generate1040(fullData, totals, investmentsData, isVerifiedPaid);
      
    } catch (error) {
      console.error("PDF Generation Error:", error);
      alert("There was an error generating your PDF.");
    } finally {
      setIsPdfLoading(false);
    }
  };

  return (
    <div className="filing-page">
      <div className="content-box-header">
        <h2><Send size={20} className="icon-orange" /> Ready to File Your Return?</h2>
      </div>
      
      <p className="review-intro">Please review your final numbers below. You can download a preview of your Form 1040 to print and mail.</p>
      
      <div className="review-summary">
        <div className="review-item">
          <span>Federal {isRefund ? 'Refund' : 'Tax Owed'}:</span>
          <strong className={isRefund ? 'refund' : 'tax'}>{formatCurrency(Math.abs(totals.finalTaxOwed))}</strong>
        </div>
      </div>

      {/* --- Personal Info Component --- */}
      <PersonalInfo formData={formData} onDataChange={onDataChange} />

      {/* --- Paid Preparer Use Only --- */}
      <div className="dependent-card" style={{ marginBottom: '2rem', textAlign: 'left' }}>
        <div className="content-box-header">
          <h4><Briefcase size={18} className="icon-orange" /> Paid Preparer Use Only</h4>
        </div>
        
        <div className="form-group">
          <label>Did you pay a professional to prepare this return?</label>
          <div className="radio-group">
            <input type="radio" id="preparerNo" name="paidPreparer.usedPaidPreparer" value="false" checked={preparer.usedPaidPreparer === false} onChange={(e) => handleRadioChange(e, 'paidPreparer.usedPaidPreparer')} />
            <label htmlFor="preparerNo">No (Self-Prepared)</label>
            <input type="radio" id="preparerYes" name="paidPreparer.usedPaidPreparer" value="true" checked={preparer.usedPaidPreparer === true} onChange={(e) => handleRadioChange(e, 'paidPreparer.usedPaidPreparer')} />
            <label htmlFor="preparerYes">Yes</label>
          </div>
        </div>

        {preparer.usedPaidPreparer && (
          <div style={{ marginTop: '1.5rem' }}>
            <div className="form-row">
              <div className="form-group"><label>Preparer's Name</label><input type="text" name="paidPreparer.preparerName" value={preparer.preparerName || ''} onChange={onDataChange} /></div>
              <div className="form-group"><label>PTIN</label><input type="text" name="paidPreparer.ptin" value={preparer.ptin || ''} onChange={onDataChange} placeholder="P########" /></div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                  <label>Date</label>
                  <input type="date" name="paidPreparer.date" value={preparer.date || ''} onChange={onDataChange} />
              </div>
              <div className="form-group checkbox-group" style={{ justifyContent: 'center' }}>
                  <input type="checkbox" id="selfEmployed" name="paidPreparer.selfEmployed" checked={preparer.selfEmployed || false} onChange={onDataChange} />
                  <label htmlFor="selfEmployed">Check if Self-Employed</label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group"><label>Firm's Name</label><input type="text" name="paidPreparer.firmName" value={preparer.firmName || ''} onChange={onDataChange} /></div>
              <div className="form-group"><label>Firm's EIN</label><input type="text" name="paidPreparer.firmEin" value={preparer.firmEin || ''} onChange={onDataChange} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Firm's Address</label><input type="text" name="paidPreparer.address" value={preparer.address || ''} onChange={onDataChange} /></div>
              <div className="form-group"><label>Phone</label><input type="tel" name="paidPreparer.phone" value={preparer.phone || ''} onChange={onDataChange} /></div>
            </div>
          </div>
        )}
      </div>

      {/* --- PDF Download Section --- */}
      <div className="dependent-card" style={{ marginBottom: '2rem', textAlign: 'center', backgroundColor: '#fcfaf8ff', border: '1px dashed #e1d5cbff' }}>
        <h4 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <FileText size={18} /> Download Your Return
        </h4>
        
        {/* 1. FREE OPTION (Always Visible) */}
        <button 
          className="add-item-button" 
          onClick={() => handleDownloadPdf(true)} // Force Draft
          disabled={isPdfLoading}
          style={{ margin: '0 auto 2rem auto', padding: '10px 20px', fontSize: '1rem', backgroundColor: '#f3f4f6', color: '#4b5563', border: '1px solid #d1d5db' }}
        >
          <Download size={18} style={{ marginRight: '8px' }} />
          <span>Download Draft (Watermarked)</span>
        </button>

        {/* 2. PAID OPTION (Conditional) */}
        {!isPro ? (
          <div style={{ 
            border: '1px solid #fca5a5', // Red Border
            padding: '2rem', 
            borderRadius: '8px', 
            backgroundColor: '#fef2f2', // Light Red Background
            maxWidth: '500px', 
            margin: '0 auto' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                <Lock size={32} color="#ef4444" /> {/* Red Icon */}
            </div>
            <h3 style={{ color: '#991b1b', marginTop: 0, fontSize: '1.25rem' }}>Unlock Official Filing PDF</h3>
            <p style={{ marginBottom: '1.5rem', color: '#7f1d1d', fontSize: '0.95rem', lineHeight: '1.5' }}>
              Get the filing-ready PDF without watermarks. Includes Schedule 1, 2, 3, A, B, C, D, E, and SE.
            </p>
            
            {/* Buy Button - RED */}
            <a 
              href="https://crispapples.gumroad.com/l/qltpwj" 
              target="_blank" 
              rel="noreferrer"
              className="ready-to-file-button"
              style={{ 
                textDecoration: 'none', 
                display: 'inline-flex', 
                justifyContent: 'center', 
                marginBottom: '1.5rem', 
                width: '100%',
                backgroundColor: '#ef4444', // Red Background
                color: 'white',
                border: 'none'
              }}
            >
              <span>Buy License Key ($19)</span>
            </a>

            <div style={{ borderTop: '1px solid #fca5a5', paddingTop: '1.5rem' }}>
                <p style={{ fontSize: '0.9rem', color: '#991b1b', marginBottom: '0.5rem', fontWeight: '600' }}>Already purchased?</p>
                <div style={{ display: 'flex', gap: '10px' }}>
                <input 
                    type="text" 
                    placeholder="Paste License Key here..." 
                    value={licenseKey}
                    onChange={(e) => setLicenseKey(e.target.value)}
                    style={{ flex: 1, padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <button 
                    onClick={handleUnlock} 
                    disabled={isValidating}
                    style={{ padding: '10px 20px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                >
                    {isValidating ? '...' : 'Activate'}
                </button>
                </div>
                <small style={{ display: 'block', marginTop: '10px', color: '#7f1d1d' }}>
                Key is sent to your email immediately after purchase.
                </small>
            </div>
          </div>
        ) : (
          <div style={{ padding: '2rem', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #16a34a', maxWidth: '500px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                <Unlock size={32} color="#16a34a" />
            </div>
            <h3 style={{ color: '#166534', marginTop: 0, fontSize: '1.25rem' }}>Pro License Active</h3>
            <p style={{ color: '#15803d', marginBottom: '1.5rem' }}>Your return is ready for official filing.</p>
            
            <button 
              className="ready-to-file-button" 
              onClick={() => handleDownloadPdf(false)} // Use actual paid status
              disabled={isPdfLoading}
              style={{ margin: '0 auto', backgroundColor: '#16a34a', width: '100%', justifyContent: 'center' }}
            >
              {isPdfLoading ? (
                  <span>Generating PDF...</span>
              ) : (
                  <>
                    <CheckCircle size={18} style={{ marginRight: '8px' }} />
                    <span>Download Official PDF</span>
                  </>
              )}
            </button>
          </div>
        )}

        <div style={{ marginTop: '2rem', textAlign: 'left', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #eee' }}>
            <h5 style={{ marginTop: 0, color: '#2d3748' }}>Filing Instructions:</h5>
            <ol style={{ paddingLeft: '1.5rem', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                <li><strong>Print:</strong> Click the button above to download and print your Form 1040 and attached schedules.</li>
                <li><strong>Sign:</strong> You (and your spouse if filing jointly) must sign and date the return on <strong>Page 2</strong>.</li>
                <li><strong>Attach:</strong> Staple a copy of your W-2s and any 1099s showing federal tax withheld to the front of the return (to the left of Line 1).</li>
                <li><strong>Mail:</strong> The mailing address depends on your state and whether you are enclosing a payment.
                    <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff7ed', borderLeft: '4px solid #EB8258', borderRadius: '4px' }}>
                        <strong>Find your address here:</strong><br/>
                        <a href="https://www.irs.gov/filing/where-to-file-paper-tax-returns-with-or-without-a-payment" target="_blank" rel="noopener noreferrer" style={{ color: '#c2410c', textDecoration: 'underline' }}>
                            IRS.gov - Where to File Paper Tax Returns
                        </a>
                    </div>
                </li>
            </ol>
        </div>
      </div>
    </div>
  );
}

export default FilingPage;